import { BANNER_LINK_TYPES, HOME_INFO_TYPES } from '@/configs/constants';
import { setActiveOrderStatus } from '@/redux/slices/order';
import { getOrderStatus } from '@/service/order.service';
import { format, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Timestamp } from 'firebase/firestore';
import path from 'path';
import apolloClient from '@/lib/graphql/apolloClient';
import { GET_PRODUCT_DETAILS } from '@/lib/graphql/query/productDetails';

const formatCurrentTime = () => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    const milliSeconds = now.getMilliseconds();
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12 || 12;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${milliSeconds.toString().padStart(2, '0')} ${ampm}`;
};

const getCallerFilePath = () => {
    try {
        const stack = new Error().stack;
        const callerLine = stack?.split('\n')?.[3]?.trim(); // Adjusted index to ensure correct line

        if (callerLine?.includes('at')) {
            const atIndex = callerLine.indexOf('at') + 3;
            const filePath = callerLine.substring(atIndex, callerLine.lastIndexOf(':')).trim();
            return filePath;
        }
    } catch (error) {
        console.error('[DebugLog]: Error extracting caller file path.', error);
    }
    return '';
};

export const debugLog = ({ level = 'warn', message, params = {}, hasTimestamp = false, hasFilePath = false }) => {
    if (process.env.NEXT_PUBLIC_ENV === 'prod') return;

    try {
        let logPrefix = '';

        if (hasTimestamp) {
            const timestamp = hasTimestamp ? `[${formatCurrentTime()}]` : '';
            logPrefix = timestamp;
        }
        if (hasFilePath) {
            const filePath = getCallerFilePath();
            const callerFile = path.basename(filePath.slice(0, 1024));
            const callerFolder = path.basename(path.dirname(filePath.slice(0, 1024)));

            logPrefix += ` [${callerFolder}/${callerFile}]`;
        }

        const logMessage = `${logPrefix} ${message}`;

        switch (level.toLowerCase()) {
            case 'error':
                console.error(logMessage, params);
                break;
            default:
                break;
        }
    } catch (error) {
        console.error('[DebugLog]: Error in logging logic.', error, { level, message, params });
    }
};

export const validatePhoneNumber = (number) => {
    const phoneRegex = /^\+?\d{7,15}$/;
    return phoneRegex.test(number);
};

export default async function loadMessages(locale) {
    try {
        return (await import(`@/lib/messages/langs/${locale}.json`)).default;
    } catch (error) {
        console.error('Error loading messages:', { ...error, locale });
        return {};
    }
}

export const currencyFormatter = (code, amount) => {
    const currency = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        INR: '₹',
        UAE: 'AED',
    };
    return currency[code] ? `${currency[code]}${amount}` : `${amount} ${code}`;
};

// Get Distance
export const getDistanceFromLatLonInKm = (customerLat, customerLong, outletLat, outletLong) => {
    const R = 6371; // Radius of Earth in km
    const dLat = degToRad(outletLat - customerLat);
    const dLong = degToRad(outletLong - customerLong);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(degToRad(customerLat)) * Math.cos(degToRad(outletLat)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let distance = roundWith(R * c);
    return distance;
};

function degToRad(deg) {
    return deg * (Math.PI / 180);
}

function roundWith(num, places = 2) {
    return parseFloat(num.toFixed(places));
}

export const calculateBiteCoinAmount = (loyalty, biteCoinConfig) => {
    return parseFloat((loyalty * (biteCoinConfig?.conversion || biteCoinConfig?.coinValue)) / 100) || 0;
};
export const generateHomeData = async (homeData, orderType, country, companyId, storeId) => {
    debugLog({
        message: 'fetchHomeData generateHomeData',
        params: {
            homeData,
            orderType,
            country,
            companyId,
            storeId,
        },
    });
    const categoryIds = [];
    const homeDataObject = homeData.reduce((obj, item) => {
        const key = item.type;
        if (!obj[key]) obj[key] = [];
        const isAvailable = checkAvailability(item.type, item.data, orderType, categoryIds, country);
        if (isAvailable) {
            if (item.type === HOME_INFO_TYPES.CATEGORY) {
                categoryIds.push(item.data.id);
            }
            obj[key].push(item.data);
        }
        return obj;
    }, {});

    // If we have companyId and storeId, filter categories with no products
    if (companyId && homeDataObject.CATEGORY?.length > 0) {
        try {
            const { getCompanyProducts } = await import('@/service/product.service');
            const categoryIds = homeDataObject.CATEGORY.map((cat) => cat.id);

            const productCounts = await Promise.all(
                categoryIds.map(async (categoryId) => {
                    try {
                        return new Promise((resolve) => {
                            getCompanyProducts(
                                {
                                    companyId,
                                    categoryIds: [categoryId],
                                    storeId: storeId || null,
                                    orderType,
                                    country,
                                },
                                (products) => {
                                    resolve({ categoryId, count: products?.length || 0 });
                                }
                            ).catch(() => {
                                resolve({ categoryId, count: 0 });
                            });
                        });
                    } catch (error) {
                        console.error(`Error fetching products for category ${categoryId}:`, error);
                        return { categoryId, count: 0 };
                    }
                })
            );

            // Filter out categories with no products
            const categoriesWithProducts = homeDataObject.CATEGORY.filter((category) => {
                const productCount = productCounts.find((pc) => pc.categoryId === category.id);
                return productCount && productCount.count > 0;
            });

            // Update homeDataObject with filtered categories
            homeDataObject.CATEGORY = categoriesWithProducts;
        } catch (error) {
            console.error('Error filtering categories by product count:', error);
            // Continue with original categories if there's an error
        }
    }

    // Get filtered banner data based on category availability
    let filteredBannerData = getCategoryBanners(homeDataObject);

    // Validate PRODUCT type banners (only for normal banners, not WELCOME_BANNER)
    if (filteredBannerData?.length > 0 && companyId) {
        try {
            const validationResults = await Promise.all(
                filteredBannerData.map(async (banner) => {
                    // Only validate normal banners (not WELCOME_BANNER)
                    if (banner.type === 'WELCOME_BANNER') {
                        const isValid = await validateProductBanner(banner, orderType, companyId, storeId, country);
                        return { banner, isValid }; // Welcome banners are handled separately
                    }

                    const parsed = parseLink(banner.link);
                    // Only validate PRODUCT type banners
                    if (parsed?.type === 'PRODUCT') {
                        const isValid = await validateProductBanner(banner, orderType, companyId, storeId, country);
                        return { banner, isValid };
                    }

                    return { banner, isValid: true }; // Non-PRODUCT banners are always valid
                })
            );

            // Filter out invalid banners
            filteredBannerData = validationResults.filter((result) => result.isValid).map((result) => result.banner);
        } catch (error) {
            console.error('Error validating product banners:', error);
            // Continue with original banners if validation fails
        }
    }

    homeDataObject.BANNER = filteredBannerData;

    Object.keys(homeDataObject).forEach((key) => {
        homeDataObject[key].sort((a, b) => a.sortOrder - b.sortOrder);
    });
    return homeDataObject;
};

const checkAvailability = (type, data, orderType, categoryIds, country) => {
    switch (type) {
        case HOME_INFO_TYPES.CHEF_PICKS:
            return isTimedAvailable(data, orderType, country) && categoryIds.includes(data.categoryId);
        case HOME_INFO_TYPES.BANNER:
            return isBannerAvailable(data, country);
        case HOME_INFO_TYPES.CATEGORY:
            if (data?.isHidden === true) {
                return false;
            }
            return isTimedAvailable(data, orderType, country);
        case HOME_INFO_TYPES.TESTIMONIALS:
            return true; // Testimonials are always available
        default:
            return false;
    }
};

/**
 * Adjusts a UTC date to a specified time zone.
 * @param {Date} date - The UTC date to adjust.
 * @param {string} timeZone - The target time zone (e.g., 'Asia/Kolkata').
 * @returns {Date} - The adjusted date in the specified time zone.
 */
export const convertToTimeZone = (date, timeZone) => {
    return new Date(date.toLocaleString('en-US', { timeZone }));
};

export const isTimedAvailable = (item, orderType, country) => {
    if (item?.isAvailableInStore === false) return false;
    if (!item?.services?.includes(orderType)) return false;

    // Non-timed items are always available
    if (!item?.timed) return true;

    // Check availability based on date and time
    const { start, end } = item?.availableDate || {};
    const currentDate = Timestamp.now();
    const currentDateUtc = new Date(currentDate.seconds * 1000); // Convert to milliseconds for Date
    currentDateUtc.setSeconds(0, 0); // Strip nanoseconds and seconds
    const now = convertToTimeZone(currentDateUtc, country?.timezone);

    // Convert start and end to Date objects if they are string representations of dates
    let startDate = null;
    let endDate = null;

    if (start) {
        startDate = convertToTimeZone(new Date(start.seconds * 1000), country?.timezone);
    }

    if (end) {
        endDate = convertToTimeZone(new Date(end.seconds * 1000), country?.timezone);
    }

    if (startDate && endDate && endDate <= startDate) {
        endDate = new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
    }

    return item?.availableToday && startDate && endDate && startDate <= now && endDate >= now;
};

const isBannerAvailable = (banner, country) => {
    const { startDate, endDate, day } = banner || {};
    const currentDate = Timestamp.now();
    const currentDateUtc = new Date(currentDate.seconds * 1000); // Convert to milliseconds for Date
    currentDateUtc.setSeconds(0, 0); // Strip nanoseconds and seconds
    const now = convertToTimeZone(currentDateUtc, country?.timezone);

    const start = convertToTimeZone(new Date(startDate.seconds * 1000), country?.timezone);
    const end = convertToTimeZone(new Date(endDate.seconds * 1000), country?.timezone);

    // Check date range validity
    const dateValid = start <= now && end >= now;

    // Check day validity
    let dayValid = true;
    if (day?.days && day.days.length > 0) {
        const currentDayShort = now.toLocaleString('en-US', { weekday: 'short' }).toUpperCase(); // e.g. 'MON'
        const currentDayLong = now.toLocaleString('en-US', { weekday: 'long' }); // e.g. 'Monday'

        // Check both short and long day formats
        dayValid = day.days.includes(currentDayShort) || day.days.includes(currentDayLong);
    }

    const result = dateValid && dayValid;
    return result;
};

/**
 * Parses a link string into an object containing the type and id.
 * @param {string} link - A string in the format of "type/id", e.g. "CATEGORY/1"
 * @returns {object} - An object with the type in uppercase and id as a number.
 * @example
 * parseLink("category/1") // { type: "CATEGORY", id: 1 }
 * parseLink("invalid") // null
 */
function parseLink(link) {
    if (!link) return null;
    const [type, id] = link.split('/');
    return { type: type?.toUpperCase().trim(), id: Number(id) };
}

/**
 * A HYPERLINK banner stores a raw URL in `link` rather than a "TYPE/docId" pair,
 * so it must never be run through parseLink.
 * @param {object} banner - A banner data object from home.BANNER.
 * @returns {boolean}
 */
export function isHyperlinkBanner(banner) {
    return banner?.bannerType === BANNER_LINK_TYPES.HYPERLINK;
}

/**
 * Returns a safe absolute http(s) URL, or null when the value is unusable.
 * The admin form accepts free text, so anything that isn't plain web http(s) —
 * custom schemes, bare domains, and `javascript:` / `data:` — is rejected here.
 * @param {string} link - The raw link value from a banner.
 * @returns {string|null}
 * @example
 * getSafeExternalUrl('https://chicking.com') // 'https://chicking.com/'
 * getSafeExternalUrl('example.com')          // null (no scheme)
 * getSafeExternalUrl('javascript:alert(1)')  // null (unsafe scheme)
 */
export function getSafeExternalUrl(link) {
    if (!link || typeof link !== 'string') return null;
    try {
        const url = new URL(link.trim());
        return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
    } catch {
        return null; // not an absolute URL
    }
}

/**
 * Opens a hyperlink banner's target in a new tab, leaving the storefront
 * (and the customer's cart/session) intact in the current one.
 * @param {object} banner - A banner data object from home.BANNER.
 * @returns {boolean} Whether navigation actually happened.
 */
export function openBannerHyperlink(banner) {
    const url = getSafeExternalUrl(banner?.link);
    if (!url) return false;
    window.open(url, '_blank', 'noopener');
    return true;
}

/**
 * Checks if current time is within product's availability time window
 * @param {object} productData - Product data from GraphQL query
 * @param {string} timezone - Timezone string (e.g., 'Asia/Dubai', 'Asia/Kolkata')
 * @returns {boolean} - True if current time is within the time window, false otherwise
 */
export function isProductTimeWindowValid(productData, timezone = 'UTC') {
    // If product is not timed, time window validation is not needed
    if (!productData?.timed) {
        return true;
    }

    // Check if product has time-based availability
    // Priority: categoryInfo.availability.time (daily time windows) over availableDate (date ranges)
    const timeAvailability = productData?.categoryInfo?.availability?.time;
    const availableDate = productData?.availableDate;

    // If no time window specified at all, consider it valid
    if ((!timeAvailability?.start || !timeAvailability?.end) && (!availableDate?.start || !availableDate?.end)) {
        // No time window specified, consider it valid
        return true;
    }

    try {
        const currentTime = new Date();

        let startTime = null;
        let endTime = null;

        // First, try to use daily time window (categoryInfo.availability.time)
        if (timeAvailability?.start && timeAvailability?.end) {
            // Helper function to parse time string and convert to Date in timezone
            const getTimeInTimezone = (timeStr) => {
                if (!timeStr) return null;
                try {
                    const cleanedTimeStr = timeStr.replace(/:\d{2}$/, '');
                    const timeFormats = ['HH:mm', 'hh:mm a', 'hh:mm'];

                    for (const timeFormat of timeFormats) {
                        const parsedDate = parse(cleanedTimeStr, timeFormat, new Date());
                        if (!Number.isNaN(parsedDate.getTime())) {
                            const hours = parsedDate.getHours();
                            const minutes = parsedDate.getMinutes();

                            // Get today's date in the target timezone
                            const dateStr = formatInTimeZone(currentTime, timezone, 'yyyy-MM-dd');
                            // Get the timezone offset
                            const tzOffset = formatInTimeZone(currentTime, timezone, 'XXX');

                            // Create ISO string with timezone: "2025-10-11T15:56:59+04:00"
                            const isoString = `${dateStr}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:59${tzOffset}`;

                            // Parse to Date object - this correctly handles the timezone
                            return new Date(isoString);
                        }
                    }
                    return null;
                } catch (error) {
                    console.error('Error parsing time:', error);
                    return null;
                }
            };

            startTime = getTimeInTimezone(timeAvailability.start);
            endTime = getTimeInTimezone(timeAvailability.end);
        }
        // Fall back to availableDate (full ISO date-time strings)
        else if (availableDate?.start && availableDate?.end) {
            try {
                // Handle different data formats (similar to useCountdownTimer.jsx)
                let startDate, endDate;

                // Parse start date
                if (typeof availableDate.start === 'string') {
                    // GraphQL format: direct ISO string
                    startDate = new Date(availableDate.start);
                } else if (availableDate.start.seconds) {
                    // Firebase format: object with seconds property
                    startDate = new Date(availableDate.start.seconds * 1000);
                } else {
                    startDate = null;
                }

                // Parse end date
                if (typeof availableDate.end === 'string') {
                    // GraphQL format: direct ISO string
                    endDate = new Date(availableDate.end);
                } else if (availableDate.end.seconds) {
                    // Firebase format: object with seconds property
                    endDate = new Date(availableDate.end.seconds * 1000);
                } else {
                    endDate = null;
                }

                // Check if dates are valid
                if (!startDate || isNaN(startDate.getTime()) || !endDate || isNaN(endDate.getTime())) {
                    startTime = null;
                    endTime = null;
                } else {
                    // Use the dates directly (they're already in UTC or specific timezone)
                    // Set to the last second of the specified minute for end time
                    startTime = startDate;
                    endTime = new Date(endDate);
                    endTime.setSeconds(59, 999);
                }
            } catch (error) {
                console.error('Error parsing availableDate:', error);
                startTime = null;
                endTime = null;
            }
        }

        if (!startTime || !endTime) {
            // If we can't parse times, consider it valid (fail open)
            return true;
        }

        // Get current time in milliseconds for comparison
        const now = currentTime.getTime();
        const startTimeMs = startTime.getTime();
        const endTimeMs = endTime.getTime();

        // Check if current time is within the window
        // For same-day windows (end >= start)
        if (endTimeMs >= startTimeMs) {
            return now >= startTimeMs && now <= endTimeMs;
        }

        // For cross-midnight windows (end < start, meaning it crosses midnight)
        // End time is actually on the next day
        const adjustedEndTimeMs = endTimeMs + 24 * 60 * 60 * 1000;

        // Current time is valid if:
        // - It's after start time today (now >= startTimeMs), AND
        // - It's before end time tomorrow (now <= adjustedEndTimeMs)
        if (now >= startTimeMs) {
            // We're after start time today - check if we're before tomorrow's end time
            return now <= adjustedEndTimeMs;
        } else {
            // We're before start time today - check if we're in yesterday's window
            // (i.e., after yesterday's start and before today's end)
            const yesterdayStartMs = startTimeMs - 24 * 60 * 60 * 1000;
            return now >= yesterdayStartMs && now <= endTimeMs;
        }
    } catch (error) {
        console.error('Error validating product time window:', error);
        // On error, consider it valid (fail open)
        return true;
    }
}

/**
 * Validates a PRODUCT type banner by checking if product is available for current order type
 * @param {object} banner - Banner object
 * @param {string} orderType - Current order type (DELIVERY, TAKEAWAY, etc.)
 * @param {string} companyId - Company ID
 * @param {string} storeId - Store ID
 * @param {object} country - Optional country object with timezone (similar to isBannerAvailable)
 * @returns {Promise<boolean>} - True if banner should be shown, false otherwise
 */
async function validateProductBanner(banner, orderType, companyId, storeId, country = null) {
    try {
        const parsed = parseLink(banner.link);
        if (!parsed || parsed.type !== 'PRODUCT' || !parsed.id) {
            return true; // Not a PRODUCT banner, always valid
        }

        const result = await apolloClient.query({
            query: GET_PRODUCT_DETAILS,
            variables: {
                id: parsed.id,
                companyId: Number(companyId),
                storeId: storeId ? Number(storeId) : null,
            },
            fetchPolicy: 'no-cache',
        });

        const productData = result?.data?.productQuery;
        if (!productData) {
            return false; // Product not found
        }

        // Check if order type matches
        if (!productData.services?.includes(orderType)) {
            return false; // Order type not supported
        }

        // Check if timed product is available today
        if (productData.timed && !productData.availableToday) {
            return false; // Timed product not available today
        }

        // Check if current time is within product's time window
        // Get timezone from country object (similar to isBannerAvailable)
        const timezone = country?.timezone || productData?.company?.timeZone || 'UTC';
        if (productData.timed && !isProductTimeWindowValid(productData, timezone)) {
            return false; // Current time is not within product's availability window
        }

        return true; // Valid product
    } catch (error) {
        console.error('Error validating product banner:', error);
        return false; // If query fails, don't show banner
    }
}

/**
 * Gets banners that are linked to categories.
 * @param {object} data - Firebase data containing CATEGORY and BANNER.
 * @returns {array} - Banners that are linked to categories.
 */
function getCategoryBanners(data) {
    if (!data?.BANNER) return [];
    // Missing CATEGORY means no category banner can match, but PRODUCT, link-less
    // and HYPERLINK banners don't depend on categories and must still come through.
    const categories = (data.CATEGORY || []).map((c) => Number(c.id ?? c.categoryId));
    return data.BANNER.filter((banner) => {
        if (isHyperlinkBanner(banner)) return true; // no in-app target to validate
        const parsed = parseLink(banner.link);
        return (
            (parsed?.type === 'CATEGORY' && categories.includes(parsed.id)) ||
            parsed?.type === 'PRODUCT' ||
            !banner.link
        );
    });
}
export const isFalsy = (value) => {
    if (!value) return true;
    if (typeof value === 'function') {
        // Functions are always truthy
        return false;
    }

    if (Array.isArray(value)) {
        return value.length === 0;
    }

    if (typeof value === 'object') {
        return Object.keys(value).length === 0;
    }

    return !value;
};

export const toSentenceCase = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const parseCoordinates = (coordinates = {}) => {
    if (isFalsy(coordinates)) {
        return {};
    }
    const { lat, lng, longitude, latitude } = coordinates;
    return {
        lat: latitude || lat || null,
        lng: longitude || lng || null,
    };
};

export const getStoreHours = (store) => {
    const { open, close } = store;

    const formatTime = (time) => {
        const [hours, minutes] = time.split(':');
        const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
        const formattedHours = (parseInt(hours) % 12 || 12).toString().padStart(2, '0');
        return `${formattedHours}:${minutes} ${ampm}`;
    };

    return {
        open: (open && formatTime(open)) || 'Opening time not available',
        close: (close && formatTime(close)) || 'Closing time not available',
    };
};

export const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number); // Split and convert to numbers
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const formatFirebaseTimestamp = (timestamp) => new Date(timestamp.seconds * 1000);
export const transformTimestamps = (data, visited = new WeakSet()) => {
    if (data === null || data === undefined) return data;

    // If it's an instance of Timestamp, convert it to ISO string
    if (data instanceof Timestamp) {
        return data.toDate().toISOString();
    }

    // If it's an object, check if we've already visited this object to avoid circular references
    if (typeof data === 'object') {
        if (visited.has(data)) {
            return data; // Return the object as is to prevent infinite recursion
        }

        visited.add(data); // Mark this object as visited

        // If it's an array, recursively transform each item
        if (Array.isArray(data)) {
            return data.map((item) => transformTimestamps(item, visited));
        }

        // If it's an object, recursively transform each property
        return Object.fromEntries(
            Object.entries(data).map(([key, value]) => [key, transformTimestamps(value, visited)])
        );
    }

    return data;
};

export const extractKeys = (obj = {}, keys = []) => {
    return keys.reduce((acc, key) => {
        if (obj[key] !== undefined) {
            acc[key] = obj[key];
        }
        return acc;
    }, {});
};

export const getSliderSettings = ({ data = [], responsive, slidesToShow = 6 }) => {
    const settings = {
        speed: 500,
        dots: false,
        slidesToShow,
        slidesToScroll: 1,
        cssEase: 'linear',
        arrows: data?.length > slidesToShow,
        infinite: data?.length > slidesToShow,
        autoplay: data?.length > slidesToShow,
        responsive: responsive || [
            {
                breakpoint: 1366,
                settings: {
                    slidesToShow: 4,
                    slidesToScroll: 3,
                    infinite: true,
                    dots: false,
                },
            },
            {
                breakpoint: 991,
                settings: {
                    slidesToShow: 4,
                    slidesToScroll: 1,
                    initialSlide: 2,
                },
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                },
            },
        ],
    };

    return settings;
};

export const getOrderStatusDetails = async (authData, dispatch) => {
    try {
        if (!authData?.uid) return;

        const response = await getOrderStatus({ customerId: authData?.uid });
        dispatch(setActiveOrderStatus(transformTimestamps(response)));
    } catch (e) {
        dispatch(
            setActiveOrderStatus({
                activeOrders: [],
            })
        );
    }
};

export const convertTimeFormat = (time, targetFormat = '24hr') => {
    if (!time) return '';

    try {
        const determineInputFormat = (timeStr) => {
            // Normalize the input string
            const normalizedTime = timeStr
                .trim()
                // Replace multiple spaces with single space
                .replace(/\s+/g, ' ')
                // Remove any periods from AM/PM
                .replace(/\./g, '')
                // Normalize AM/PM format
                .replace(/([AaPp])\.?[Mm]\.?/, (match) => match.charAt(0).toUpperCase() + 'M');

            // Strict check for AM/PM at the end of string only
            const hasAMPM = /\s[AaPp]M$/.test(normalizedTime);

            // Pattern for hours with optional leading zero
            const hour12Pattern = '(0?[1-9]|1[0-2])';
            const hour24Pattern = '([01]?[0-9]|2[0-3])';

            // Pattern for minutes with required two digits
            const minutePattern = '[0-5][0-9]';

            // Pattern for optional seconds
            const secondsPattern = '(?::[0-5][0-9])?';

            // Build complete patterns
            const pattern12hr = new RegExp(`^${hour12Pattern}:${minutePattern}\\s[AaPp]M$`);
            const pattern24hr = new RegExp(`^${hour24Pattern}:${minutePattern}${secondsPattern}$`);

            // Validate format
            const is12Hour = hasAMPM && pattern12hr.test(normalizedTime);
            const is24Hour = !hasAMPM && pattern24hr.test(normalizedTime);

            if (!is12Hour && !is24Hour) {
                throw new Error(
                    'Invalid time format. Use "HH:MM" or "HH:MM:SS" for 24-hour or "HH:MM AM/PM" for 12-hour format.'
                );
            }

            // Determine if the 24-hour format includes seconds
            const hasSeconds = is24Hour && normalizedTime.split(':').length === 3;

            let timeFormat;
            if (is12Hour) {
                timeFormat = 'hh:mm a';
            } else {
                timeFormat = hasSeconds ? 'HH:mm:ss' : 'HH:mm';
            }

            return {
                format: timeFormat,
                normalizedTime,
            };
        };

        const { format: inputFormat, normalizedTime } = determineInputFormat(time);

        if (targetFormat === '12hr') {
            const parsedTime = parse(normalizedTime, inputFormat, new Date());
            return format(parsedTime, 'hh:mm a');
        } else if (targetFormat === '24hr') {
            const parsedTime = parse(normalizedTime, inputFormat, new Date());
            // If input was in HH:mm:ss format, preserve seconds in output
            const hasInputSeconds = inputFormat === 'HH:mm:ss';
            return format(parsedTime, hasInputSeconds ? 'HH:mm:ss' : 'HH:mm');
        } else {
            throw new Error('Invalid target format. Use "12hr" or "24hr".');
        }
    } catch (error) {
        console.error('Time conversion error:', error);
        return `Error: ${error.message}`;
    }
};

export const is24HourFormat = (time) => {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
};

export const isCategoryAvailableNow = (availability, timezone) => {
    if (!availability) return false;
    const { day, date, time } = availability;
    const currentDate = Timestamp.now();
    const now = convertToTimeZone(new Date(currentDate.seconds * 1000), timezone);

    // Validate date range
    let dateValid = true;
    if (date?.start && date?.end) {
        // Accept both string and Timestamp
        const startDate = convertToTimeZone(
            typeof date.start === 'string' ? new Date(date.start) : new Date(date.start.seconds * 1000),
            timezone
        );
        const endDate = convertToTimeZone(
            typeof date.end === 'string' ? new Date(date.end) : new Date(date.end.seconds * 1000),
            timezone
        );
        // If start and end date are the same, treat the whole day as valid
        if (startDate.toDateString() === endDate.toDateString()) {
            dateValid = now.toDateString() === startDate.toDateString();
        } else {
            dateValid = startDate <= now && endDate >= now;
        }

        if (!dateValid) {
            console.error('This category is not valid in the current date');
        }
    }

    // Validate day
    let dayValid = true;
    if (day?.days && day.days.length > 0) {
        const currentDay = now.toLocaleString('en-US', { weekday: 'short' }).toUpperCase(); // e.g. 'MON'
        dayValid = day.days.includes(currentDay);
        if (!dayValid) {
            console.error('This category is not valid on the current day');
        }
    }

    // Validate time range
    let timeValid = true;
    if (time?.start?.toString && time?.end?.toString) {
        // Accept both string and Timestamp
        let startTime =
            typeof time?.start === 'string'
                ? time?.start
                : new Date(time?.start?.seconds * 1000).toTimeString().slice(0, 5);
        let endTime =
            typeof time?.end === 'string'
                ? time?.end
                : new Date(time?.end?.seconds * 1000).toTimeString().slice(0, 5());
        // Get current time in HH:mm format
        const currentTime =
            now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
        // If start and end time are the same, treat the time is valid
        if (startTime === endTime) {
            timeValid = true;
        } else {
            timeValid = startTime <= currentTime && endTime >= currentTime;
        }
        if (!timeValid) {
            console.error('This category is not valid in the current time');
        }
    }

    return dateValid && dayValid && timeValid;
};
